import { normalizeName } from "../parser/item-input.js";
import type { PantryItem, TripItem, Unit } from "../types/entities.js";
import { convert, sameDimension } from "../units/convert.js";

/**
 * Der Vorrat, und was mit ihm passiert.
 *
 * Zwei Bewegungen, beide automatisch: Ein abgeschlossener Einkauf füllt auf,
 * was als Vorrat geführt wird, und ein Bestand unter dem Mindestbestand wird
 * zum Vorschlag für die Liste. Alles andere trägt die Familie von Hand ein.
 */

/** Ein Vorrat, der zur Neige geht. */
export interface LowStock {
  item: PantryItem;
  /** Wie viel fehlt bis zum Mindestbestand, in der Einheit des Vorrats. */
  missing: number;
}

/**
 * Was unter den Mindestbestand gerutscht ist.
 *
 * Ohne Mindestbestand gibt es keine Warnung: Nicht jeder Vorrat muss immer
 * da sein, und eine Liste voller Vorschläge ist keine Hilfe.
 */
export function lowStock(pantry: readonly PantryItem[]): LowStock[] {
  return pantry
    .filter((item) => item.deleted_at === null && item.min_quantity !== null && item.quantity < item.min_quantity)
    .map((item) => ({ item, missing: runde((item.min_quantity ?? 0) - item.quantity) }))
    .sort((a, b) => a.item.name.localeCompare(b.item.name, "de-DE"));
}

/** Eine Bestandsänderung, die der Aufrufer schreibt. */
export interface StockChange {
  id: string;
  quantity: number;
}

/**
 * Was ein abgeschlossener Einkauf dem Vorrat hinzufügt.
 *
 * Aufgefüllt wird nur, was schon als Vorrat geführt wird. Ein Einkauf legt
 * keinen neuen Vorrat an: Sonst stünde nach drei Wochen jede Tüte Chips in
 * der Vorratsliste, und niemand pflegt das je wieder zurück.
 *
 * Passt die Einheit des Einkaufs nicht zur Einheit des Vorrats, wird nicht
 * gerechnet. Der Bestand bleibt, wie er war, statt falsch zu werden.
 */
export function restockAfterTrip(pantry: readonly PantryItem[], tripItems: readonly TripItem[]): StockChange[] {
  const offen = pantry.filter((item) => item.deleted_at === null);
  const zuwachs = new Map<string, number>();

  for (const position of tripItems) {
    if (position.deleted_at !== null) continue;

    const treffer = offen.find(
      (item) =>
        (position.product_id !== null && item.product_id === position.product_id) ||
        item.normalized_name === normalizeName(position.name),
    );
    if (!treffer) continue;

    const menge = mengeFuer(position, treffer.unit);
    if (menge === null) continue;

    zuwachs.set(treffer.id, (zuwachs.get(treffer.id) ?? 0) + menge);
  }

  return [...zuwachs]
    .map(([id, plus]) => {
      const vorher = offen.find((item) => item.id === id)?.quantity ?? 0;
      return { id, quantity: runde(vorher + plus) };
    })
    .sort((a, b) => a.id.localeCompare(b.id));
}

/**
 * Wie viel eine Einkaufsposition dem Vorrat bringt, in dessen Einheit.
 *
 * Ohne Menge gilt eins: Wer "Mehl" abhakt, hat eine Packung gekauft, und
 * eins ist die ehrlichere Schätzung als null.
 */
function mengeFuer(position: TripItem, zielEinheit: Unit | null): number | null {
  if (position.quantity === null) return sameDimension(position.unit, zielEinheit) || position.unit === null ? 1 : null;
  return convert({ quantity: position.quantity, unit: position.unit }, zielEinheit);
}

function runde(wert: number): number {
  return Math.round(wert * 1000) / 1000 + 0;
}
